import {
  AuthInput,
  Button,
  Divider,
  InputErrorMessage,
  Link,
} from "@cynthai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { saveAuthRedirect } from "@/core/lib/auth-redirect";
import { clearAuthSession } from "@/core/lib/auth-session";
import { useLogin } from "@/features/auth/hooks/use-login";
import { useWorkosAuthorize } from "@/features/auth/hooks/use-workos-authorize";
import { m } from "@/paraglide/messages";

const searchSchema = z.object({
  redirect: z.string().optional(),
});

const schema = z.object({
  email: z.string().min(1, m.email_min_required()),
  password: z.string().min(1, m.password_min_required()),
});

function SignIn() {
  const { redirect: redirectTo } = Route.useSearch();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  if (redirectTo) {
    saveAuthRedirect(redirectTo);
  }

  const login = useLogin({
    onError: (err) => {
      setError("root", { message: err });
    },
  });

  const workos = useWorkosAuthorize(redirectTo);

  return (
    <>
      <p className="text-text-primary mb-9 text-2xl font-semibold">
        {m.sign_in()}
      </p>

      {workos.enabled && (
        <>
          <Button
            color="primary"
            className="w-full"
            size="lg"
            isLoading={workos.isLoading}
            onPress={() => {
              if (workos.authorizationUrl) {
                globalThis.location.href = workos.authorizationUrl;
              }
            }}
          >
            {m.sign_in_with_sso()}
          </Button>

          <div className="my-6 flex items-center gap-3">
            <Divider className="flex-1" />
            <span className="text-text-tertiary text-xs">{m.or()}</span>
            <Divider className="flex-1" />
          </div>
        </>
      )}

      <form
        noValidate
        onSubmit={handleSubmit((data) => {
          login.mutate({
            body: { username: data.email, password: data.password },
          });
        })}
        className="flex flex-col gap-4"
      >
        <AuthInput
          label={m.email()}
          {...register("email")}
          errorMessage={errors.email?.message}
          isInvalid={errors.root?.message !== undefined}
        />
        <AuthInput
          label={m.password()}
          {...register("password")}
          errorMessage={errors.password?.message}
          isInvalid={errors.root?.message !== undefined}
          hiddable
        />
        <Link to="/auth/forgot-password" className="ml-auto">
          {m.forgot_password()}
        </Link>
        {errors.root?.message && (
          <InputErrorMessage>{errors.root.message}</InputErrorMessage>
        )}
        <Button
          color="primary"
          className="mt-12 w-full"
          size="lg"
          type="submit"
        >
          {m.sign_in()}
        </Button>
      </form>
      <div className="mt-4 flex items-center gap-4">
        <p className="text-text-light text-xs font-light">{m.no_account()}</p>
        <Link to="/auth/sign-up">{m.sign_up()}</Link>
      </div>
    </>
  );
}

export const Route = createFileRoute("/auth/sign-in")({
  component: () => <SignIn />,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }
    clearAuthSession();
  },
});
